// frontend/src/pages/configuraciones/ConfiguracionMapeoGPS.jsx
// Vincula equipos del sistema con vehículos del proveedor GPS.
// Flujo: elegir conector → cargar vehículos del proveedor → hacer match contra equipos del sistema.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import apiClient from '../../services/apiClient';
import gpsService from '../../services/gpsService';
import equiposService from '../../services/equiposService';
import HelpPanel, { HelpToggleBtn } from '../../components/common/HelpPanel';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';

// ─── Styled ───────────────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 0.85rem 1rem 1rem;
  background: ${({ theme }) => theme.colors.bgPrincipal};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  display: flex; flex-direction: column; gap: 0.75rem;
  min-height: calc((100vh / var(--app-zoom, 1)) - 80px);
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 0.45rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.info};
`;
const Title = styled.h1`
  color: ${({ theme }) => theme.colors.info}; font-size: 1.25rem;
  margin: 0; font-weight: 500; display: flex; align-items: center; gap: 0.45rem;
`;
const BackBtn = styled.button`
  background: none; border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtSecondary}; border-radius: 4px;
  padding: 0.3rem 0.75rem; font-size: 0.8rem; cursor: pointer;
  display: flex; align-items: center; gap: 0.35rem;
  &:hover { border-color: ${({ theme }) => theme.colors.info}; color: ${({ theme }) => theme.colors.info}; }
`;
const Toolbar = styled.div`
  display: flex; gap: 0.6rem; align-items: center; flex-wrap: wrap;
`;
const StyledSelect = styled.select`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.4rem 0.65rem; border-radius: 4px; font-size: 0.85rem; cursor: pointer; min-width: 260px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
  option { background: ${({ theme }) => theme.colors.surface}; }
`;
const spinAnim = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const PrimaryBtn = styled.button`
  background: ${({ theme }) => theme.colors.info}; color: ${({ theme }) => theme.colors.onPrimary};
  border: none; padding: 0.4rem 0.9rem; border-radius: 4px;
  font-size: 0.8rem; font-weight: 500; cursor: pointer;
  display: flex; align-items: center; gap: 0.35rem;
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  i.spin { animation: ${spinAnim} 0.8s linear infinite; }
`;
const SearchInput = styled.input`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.35rem 0.6rem; border-radius: 4px; font-size: 0.8rem; width: 180px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
  &::placeholder { color: ${({ theme }) => theme.colors.txtSecundario}; }
`;
const InfoChip = styled.div`
  font-size: 0.78rem; color: ${({ theme }) => theme.colors.txtSecondary};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 4px; padding: 0.35rem 0.65rem;
`;
const ErrorBanner = styled.div`
  background: ${({ theme }) => `${theme.colors.danger}18`};
  border: 1px solid ${({ theme }) => `${theme.colors.danger}55`};
  border-radius: 6px; padding: 0.6rem 0.85rem; font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.danger};
  display: flex; align-items: center; gap: 0.5rem;
`;

// ─── Tabla de vinculaciones ────────────────────────────────────────────────────

const TableWrap = styled.div`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 8px; overflow: hidden; flex: 1; overflow-y: auto;
`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Th = styled.th`
  padding: 0.5rem 0.65rem; text-align: left; font-weight: 600;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-bottom: 2px solid ${({ theme }) => theme.colors.borderDefault};
  font-size: 0.78rem; background: ${({ theme }) => theme.colors.bgSecundario};
  position: sticky; top: 0; z-index: 1; white-space: nowrap;
`;
const Td = styled.td`
  padding: 0.45rem 0.65rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  font-size: 0.8rem; vertical-align: middle;
`;
const ActionsCell = styled(Td)`
  text-align: right; white-space: nowrap;
  & > * { opacity: 0; pointer-events: none; transition: opacity 0.15s; }
  tr:hover & > *, tr:focus-within & > * { opacity: 1; pointer-events: auto; }
`;
const ActionBtn = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  border-radius: 4px; padding: 2px 8px; font-size: 0.72rem; cursor: pointer;
  &:hover { background: ${({ theme }) => `${theme.colors.danger}18`}; }
`;
const Empty = styled.div`
  text-align: center; padding: 2.5rem; color: ${({ theme }) => theme.colors.txtSecondary}; font-size: 0.85rem;
`;

// ─── Tabla de matching (modal) ─────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed; inset: 0;
  background: ${({ theme }) => theme.colors.overlayBackdropStrong};
  display: flex; align-items: center; justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal}; padding: 1rem;
`;
const ModalBox = styled.div`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 8px; width: 100%; max-width: 900px;
  display: flex; flex-direction: column;
  max-height: calc((100vh / var(--app-zoom, 1)) - 2rem);
`;
const ModalHead = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.75rem 1rem; border-bottom: 1px solid ${({ theme }) => theme.colors.info};
  flex-shrink: 0;
`;
const ModalTitle = styled.h2`margin: 0; font-size: 1.05rem; font-weight: 500; color: ${({ theme }) => theme.colors.info};`;
const CloseBtn = styled.button`
  background: none; border: none; font-size: 1.4rem; cursor: pointer;
  color: ${({ theme }) => theme.colors.txtSecondary};
  &:hover { color: ${({ theme }) => theme.colors.danger}; }
`;
const ModalToolbar = styled.div`
  padding: 0.6rem 1rem; border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  display: flex; gap: 0.5rem; align-items: center; flex-shrink: 0; flex-wrap: wrap;
`;
const ModalBody = styled.div`flex: 1; overflow-y: auto;`;
const ModalFoot = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 0.5rem;
  padding: 0.65rem 1rem; border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  flex-shrink: 0;
`;
const PendingCount = styled.span`
  font-size: 0.82rem; color: ${({ theme }) => theme.colors.txtSecondary};
`;
const SaveBtn = styled.button`
  background: ${({ theme, $danger }) => $danger ? theme.colors.danger : theme.colors.success};
  color: ${({ theme }) => theme.colors.onPrimary};
  border: none; padding: 0.45rem 1.2rem; border-radius: 6px; font-size: 0.88rem; cursor: pointer;
  display: flex; align-items: center; gap: 0.4rem;
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const CancelBtn = styled.button`
  background: none; border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.45rem 1rem; border-radius: 6px; font-size: 0.88rem; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }
`;

const SuggestionBadge = styled.span`
  font-size: 0.7rem; padding: 1px 6px; border-radius: 999px;
  background: ${({ theme }) => `${theme.colors.success}22`};
  color: ${({ theme }) => theme.colors.success};
  border: 1px solid ${({ theme }) => `${theme.colors.success}55`};
  white-space: nowrap;
`;

const AlreadyBadge = styled.span`
  font-size: 0.7rem; padding: 2px 8px; border-radius: 999px;
  background: ${({ theme }) => `${theme.colors.info}18`};
  color: ${({ theme }) => theme.colors.info};
  border: 1px solid ${({ theme }) => `${theme.colors.info}55`};
  white-space: nowrap;
  display: inline-flex; align-items: center; gap: 4px;
`;

const EditLinkBtn = styled.button`
  background: none; border: none; cursor: pointer;
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-size: 0.7rem; padding: 2px 4px; border-radius: 3px;
  &:hover { color: ${({ theme }) => theme.colors.info}; }
`;

// ─── Combobox con búsqueda ─────────────────────────────────────────────────────

const ComboWrap = styled.div`position: relative; width: 100%;`;
const ComboInput = styled.input`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ $matched, $editing, theme }) =>
    $editing ? theme.colors.info : $matched ? theme.colors.success : theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.3rem 0.5rem; border-radius: 4px; font-size: 0.8rem; width: 100%;
  box-sizing: border-box;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
  &::placeholder { color: ${({ theme }) => theme.colors.txtSecundario}; }
`;
const ComboDropdown = styled.ul`
  position: absolute; top: calc(100% + 2px); left: 0; right: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.info};
  border-radius: 4px; margin: 0; padding: 2px 0;
  list-style: none; z-index: 200;
  max-height: 200px; overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;
const ComboOption = styled.li`
  padding: 0.3rem 0.55rem; font-size: 0.8rem; cursor: pointer;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  background: ${({ $active, theme }) => $active ? theme.colors.infoBg : 'transparent'};
  &:hover { background: ${({ theme }) => theme.colors.hoverRow}; }
`;
const ComboNone = styled.li`
  padding: 0.35rem 0.55rem; font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.txtSecondary}; font-style: italic;
`;

// Combobox reutilizable con búsqueda integrada
function EquipoCombobox({ equipos, value, onChange, equiposOcupados, idExterno, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  const equipoSeleccionado = equipos.find(e => String(e.id) === String(value));

  // Cuando se abre el dropdown, inicializar query con el texto del equipo ya seleccionado
  const handleFocus = () => {
    setQuery('');
    setOpen(true);
  };

  const handleBlur = (e) => {
    // Cerrar solo si el foco sale del componente completo
    if (!wrapRef.current?.contains(e.relatedTarget)) {
      setOpen(false);
      setQuery('');
    }
  };

  const opciones = useMemo(() => {
    const q = query.toLowerCase();
    return equipos.filter(eq => {
      // Excluir equipos ya ocupados por otro id_externo, pero permitir el actualmente seleccionado
      if (equiposOcupados.has(eq.id) && String(eq.id) !== String(value)) return false;
      if (!q) return true;
      return (
        eq.interno?.toLowerCase().includes(q) ||
        eq.interno_alternativo?.toLowerCase().includes(q) ||
        eq.descripcion?.toLowerCase().includes(q)
      );
    });
  }, [equipos, equiposOcupados, value, query]);

  const seleccionar = (eq) => {
    onChange(eq ? String(eq.id) : '');
    setOpen(false);
    setQuery('');
  };

  const displayValue = open ? query : (equipoSeleccionado
    ? `${equipoSeleccionado.interno}${equipoSeleccionado.descripcion ? ` — ${equipoSeleccionado.descripcion}` : ''}`
    : '');

  return (
    <ComboWrap ref={wrapRef} onBlur={handleBlur}>
      <ComboInput
        $matched={!!value}
        $editing={open}
        placeholder="— Sin vincular —"
        value={displayValue}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={handleFocus}
        disabled={disabled}
        autoComplete="off"
      />
      {open && (
        <ComboDropdown onMouseDown={e => e.preventDefault()}>
          <ComboOption
            $active={!value}
            onClick={() => seleccionar(null)}
          >
            — Sin vincular —
          </ComboOption>
          {opciones.length === 0 && <ComboNone>Sin resultados</ComboNone>}
          {opciones.map(eq => (
            <ComboOption
              key={eq.id}
              $active={String(eq.id) === String(value)}
              onClick={() => seleccionar(eq)}
            >
              {eq.interno}{eq.descripcion ? ` — ${eq.descripcion}` : ''}
            </ComboOption>
          ))}
        </ComboDropdown>
      )}
    </ComboWrap>
  );
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function ConfiguracionMapeoGPS() {
  const navigate = useNavigate();

  const [conectores, setConectores] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [mapeos, setMapeos] = useState([]);
  const [conectorId, setConectorId] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [loadingMapeos, setLoadingMapeos] = useState(false);

  // Estado del modal de vinculación
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [vehiculosProveedor, setVehiculosProveedor] = useState([]);
  // id_externo → equipo_id (string). 'EDIT:' prefix marca fila en modo edición
  const [asignaciones, setAsignaciones] = useState({});
  // id_externo → mapeo_id: filas que se van a REEMPLAZAR (editar vinculación existente)
  const [ediciones, setEdiciones] = useState({});
  const [busquedaModal, setBusquedaModal] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const { onBackdropMouseDown, buildOnBackdropClick } = useBackdropCloseGuard();

  // Carga inicial
  useEffect(() => {
    apiClient.get('conectores-gps/?page_size=200').then(r => {
      const lista = Array.isArray(r.data) ? r.data : (r.data.results || []);
      setConectores(lista);
      if (lista.length > 0) setConectorId(String(lista[0].id));
    }).catch(console.error);

    equiposService.getTodosEquipos().then(res => {
      const lista = res?.data || [];
      setEquipos(Array.isArray(lista) ? lista : []);
    }).catch(console.error);
  }, []);

  const cargarMapeos = useCallback(async (cid) => {
    if (!cid) return;
    setLoadingMapeos(true);
    try {
      const r = await apiClient.get(`mapeos-gps/?conector=${cid}&page_size=1000`);
      setMapeos(Array.isArray(r.data) ? r.data : (r.data.results || []));
    } catch (e) { console.error(e); }
    finally { setLoadingMapeos(false); }
  }, []);

  useEffect(() => { cargarMapeos(conectorId); }, [conectorId, cargarMapeos]);

  const mapeosFiltrados = useMemo(() => {
    if (!busqueda.trim()) return mapeos;
    const q = busqueda.toLowerCase();
    return mapeos.filter(m =>
      m.equipo_interno?.toLowerCase().includes(q) ||
      m.equipo_interno_alternativo?.toLowerCase().includes(q) ||
      m.id_externo?.toLowerCase().includes(q)
    );
  }, [mapeos, busqueda]);

  const conectorActual = conectores.find(c => String(c.id) === conectorId);

  // Mapa de id_externo → mapeo para lookup rápido
  const mapeosPorIdExterno = useMemo(
    () => Object.fromEntries(mapeos.map(m => [m.id_externo, m])),
    [mapeos]
  );

  // Equipos que ya están mapeados en BD (persistidos)
  const equiposMapeados = useMemo(() => new Set(mapeos.map(m => m.equipo)), [mapeos]);

  // Equipos que no se pueden elegir: mapeados en BD + pendientes en el modal
  // (pero el equipo actualmente en edición para esa fila queda excluido del bloqueo)
  const getEquiposOcupados = useCallback((idExterno) => {
    const ocupados = new Set();
    // Equipos persistidos en BD (excepto el de la fila que se está editando)
    mapeos.forEach(m => {
      // Si esta fila está siendo editada, el equipo actual queda liberado
      if (ediciones[idExterno] && m.id_externo === idExterno) return;
      ocupados.add(m.equipo);
    });
    // Equipos pendientes en otras filas del modal
    Object.entries(asignaciones).forEach(([idExt, eqId]) => {
      if (idExt !== idExterno && eqId) ocupados.add(Number(eqId));
    });
    return ocupados;
  }, [mapeos, ediciones, asignaciones]);

  // ─── Abrir modal de vinculación ──────────────────────────────────────────────

  const abrirVincular = async () => {
    setModalOpen(true);
    setLoadingPreview(true);
    setPreviewError('');
    setVehiculosProveedor([]);
    setAsignaciones({});
    setEdiciones({});
    setBusquedaModal('');
    try {
      const vs = await gpsService.previsualizarConector(conectorId);
      setVehiculosProveedor(vs);

      // Pre-asignar sugerencias automáticas: alias === equipo.interno
      const sugeridas = {};
      const idExternosMapeados = new Set(mapeos.map(m => m.id_externo));
      vs.forEach(v => {
        if (idExternosMapeados.has(v.id_externo)) return;
        const eq = equipos.find(e =>
          e.interno?.toLowerCase() === v.nombre?.toLowerCase()
        );
        if (eq && !equiposMapeados.has(eq.id)) {
          sugeridas[v.id_externo] = String(eq.id);
        }
      });
      setAsignaciones(sugeridas);
    } catch (e) {
      setPreviewError(e?.response?.data?.error || 'No se pudo obtener los vehículos del proveedor.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const vehiculosFiltrados = useMemo(() => {
    if (!busquedaModal.trim()) return vehiculosProveedor;
    const q = busquedaModal.toLowerCase();
    return vehiculosProveedor.filter(v =>
      v.nombre?.toLowerCase().includes(q) ||
      v.patente?.toLowerCase().includes(q) ||
      v.id_externo?.toLowerCase().includes(q) ||
      v.numero_serie?.toLowerCase().includes(q)
    );
  }, [vehiculosProveedor, busquedaModal]);

  const pendientes = useMemo(
    () => Object.values(asignaciones).filter(Boolean).length,
    [asignaciones]
  );

  const iniciarEdicion = (v) => {
    const mapeo = mapeosPorIdExterno[v.id_externo];
    if (!mapeo) return;
    setEdiciones(prev => ({ ...prev, [v.id_externo]: mapeo.id }));
    setAsignaciones(prev => ({ ...prev, [v.id_externo]: String(mapeo.equipo) }));
  };

  const cancelarEdicion = (idExterno) => {
    setEdiciones(prev => { const n = { ...prev }; delete n[idExterno]; return n; });
    setAsignaciones(prev => { const n = { ...prev }; delete n[idExterno]; return n; });
  };

  const guardarVinculaciones = async () => {
    const pares = Object.entries(asignaciones).filter(([, eqId]) => !!eqId);
    if (!pares.length) return;
    setSaving(true);
    for (const [id_externo, eqId] of pares) {
      try {
        if (ediciones[id_externo]) {
          // Edición: eliminar el mapeo anterior y crear uno nuevo
          await apiClient.delete(`mapeos-gps/${ediciones[id_externo]}/`);
        }
        await apiClient.post('mapeos-gps/', {
          equipo: Number(eqId),
          conector: Number(conectorId),
          id_externo,
        });
      } catch (e) {
        // duplicado u otro error → ignorar y seguir
      }
    }
    setSaving(false);
    setModalOpen(false);
    cargarMapeos(conectorId);
  };

  const eliminar = async (id) => {
    try {
      await apiClient.delete(`mapeos-gps/${id}/`);
      setConfirmId(null);
      cargarMapeos(conectorId);
    } catch (e) { console.error(e); }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Page>
      <Header>
        <Title><i className="fas fa-link" /> Vinculación GPS de equipos</Title>
        <HelpToggleBtn
          type="button"
          $active={helpOpen}
          onClick={() => setHelpOpen(v => !v)}
        >
          <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
          Ayuda
        </HelpToggleBtn>
      </Header>

      {helpOpen && (
        <div style={{ marginBottom: '1rem' }}>
          <HelpPanel
            prereqs={[
              'Tener configurado al menos un conector GPS con sus credenciales.',
            ]}
            tips={[
              'Acá se indica qué dispositivo GPS corresponde a cada equipo del parque.',
              'Un equipo mal vinculado recibe las horas de otro: verificá el número interno antes de confirmar.',
              'Los equipos sin vincular no tendrán rastreo satelital disponible en el sistema.',
            ]}
            nextSteps={[
              'Controlar que lleguen las lecturas de cada equipo vinculado.',
            ]}
            inlineOpen
          />
        </div>
      )}

      <Toolbar>
        <StyledSelect value={conectorId} onChange={e => { setConectorId(e.target.value); setBusqueda(''); }}>
          {conectores.length === 0 && <option value="">— Sin conectores —</option>}
          {conectores.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </StyledSelect>

        {conectorActual && (
          <InfoChip>
            <i className="fas fa-satellite-dish" style={{ marginRight: 4 }} />
            {conectorActual.activo ? 'Activo' : 'Inactivo'}
            · {mapeos.length} vinculados
          </InfoChip>
        )}

        <SearchInput
          placeholder="Buscar equipo o ID..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />

        <div style={{ flex: 1 }} />

        <BackBtn onClick={() => navigate('/configuracion?seccion=gps')}>
          <i className="fas fa-arrow-left" /> Volver
        </BackBtn>
        <PrimaryBtn onClick={abrirVincular} disabled={!conectorId || conectores.length === 0}>
          <i className="fas fa-link" /> Vincular equipos del proveedor
        </PrimaryBtn>
      </Toolbar>

      {/* Tabla de mapeos existentes */}
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Equipo del sistema</Th>
              <Th>ID en proveedor GPS</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loadingMapeos && <tr><Td colSpan={3}><Empty>Cargando...</Empty></Td></tr>}
            {!loadingMapeos && mapeosFiltrados.length === 0 && (
              <tr><Td colSpan={3}><Empty>
                {conectorId ? 'Sin vinculaciones. Usá el botón "Vincular equipos del proveedor".' : 'Seleccioná un conector.'}
              </Empty></Td></tr>
            )}
            {mapeosFiltrados.map(m => (
              <tr key={m.id}>
                <Td><strong>{m.equipo_interno}</strong></Td>
                <Td style={{ fontFamily: 'monospace', fontSize: '0.79rem' }}>{m.id_externo}</Td>
                <ActionsCell>
                  <ActionBtn onClick={() => setConfirmId(m.id)}>Desvincular</ActionBtn>
                </ActionsCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>

      {/* Modal de vinculación */}
      {modalOpen && (
        <Overlay onClick={buildOnBackdropClick(() => setModalOpen(false), () => !saving)} onMouseDown={onBackdropMouseDown}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>
                <i className="fas fa-link" style={{ marginRight: 6 }} />
                Vincular equipos — {conectorActual?.nombre}
              </ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={() => !saving && setModalOpen(false)}>×</CloseBtn>
            </ModalHead>

            <ModalToolbar>
              {!loadingPreview && !previewError && (
                <>
                  <SearchInput
                    placeholder="Filtrar vehículos del proveedor..."
                    value={busquedaModal}
                    onChange={e => setBusquedaModal(e.target.value)}
                    style={{ width: 240 }}
                  />
                  <InfoChip>{vehiculosProveedor.length} vehículos del proveedor</InfoChip>
                  <InfoChip style={{ color: 'inherit' }}>
                    {pendientes} asignaciones pendientes de guardar
                  </InfoChip>
                </>
              )}
            </ModalToolbar>

            <ModalBody>
              {loadingPreview && (
                <Empty style={{ padding: '3rem' }}>
                  <i className="fas fa-circle-notch fa-spin" style={{ fontSize: 28, marginBottom: 12, display: 'block' }} />
                  Conectando con el proveedor GPS…
                  <br /><small style={{ opacity: 0.7 }}>Esto puede tardar hasta 30 segundos.</small>
                </Empty>
              )}

              {previewError && (
                <div style={{ padding: '1rem' }}>
                  <ErrorBanner>
                    <i className="fas fa-exclamation-triangle" />
                    {previewError}
                  </ErrorBanner>
                </div>
              )}

              {!loadingPreview && !previewError && (
                <Table>
                  <thead>
                    <tr>
                      <Th>Vehículo en {conectorActual?.nombre}</Th>
                      <Th>Patente</Th>
                      <Th>ID externo</Th>
                      <Th>Equipo del sistema</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehiculosFiltrados.map(v => {
                      const mapeoExistente = mapeosPorIdExterno[v.id_externo];
                      const enEdicion = !!ediciones[v.id_externo];
                      const esSugerencia = !mapeoExistente && !enEdicion &&
                        asignaciones[v.id_externo] &&
                        equipos.find(e => String(e.id) === asignaciones[v.id_externo] &&
                          e.interno?.toLowerCase() === v.nombre?.toLowerCase());

                      return (
                        <tr key={v.id_externo}>
                          <Td>
                            <strong>{v.nombre}</strong>
                            {v.campos_extra?.driver && (
                              <div style={{ fontSize: '0.72rem', opacity: 0.65 }}>{v.campos_extra.driver}</div>
                            )}
                          </Td>
                          <Td style={{ fontFamily: 'monospace', fontSize: '0.79rem' }}>{v.patente || '—'}</Td>
                          <Td style={{ fontFamily: 'monospace', fontSize: '0.79rem', opacity: 0.7 }}>{v.id_externo}</Td>
                          <Td>
                            {mapeoExistente && !enEdicion ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <AlreadyBadge>
                                  <i className="fas fa-check" />
                                  {mapeoExistente.equipo_interno || 'Vinculado'}
                                </AlreadyBadge>
                                <EditLinkBtn
                                  title="Cambiar vinculación"
                                  onClick={() => iniciarEdicion(v)}
                                >
                                  <i className="fas fa-pencil-alt" />
                                </EditLinkBtn>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <EquipoCombobox
                                  equipos={equipos}
                                  value={asignaciones[v.id_externo] || ''}
                                  onChange={val => setAsignaciones(prev => ({
                                    ...prev,
                                    [v.id_externo]: val || undefined,
                                  }))}
                                  equiposOcupados={getEquiposOcupados(v.id_externo)}
                                  idExterno={v.id_externo}
                                />
                                {esSugerencia && <SuggestionBadge>sugerido</SuggestionBadge>}
                                {enEdicion && (
                                  <EditLinkBtn
                                    title="Cancelar edición"
                                    onClick={() => cancelarEdicion(v.id_externo)}
                                  >
                                    <i className="fas fa-times" />
                                  </EditLinkBtn>
                                )}
                              </div>
                            )}
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </ModalBody>

            <ModalFoot>
              <PendingCount>
                {pendientes > 0 ? `${pendientes} vinculación${pendientes > 1 ? 'es' : ''} lista${pendientes > 1 ? 's' : ''} para guardar` : 'Asigná equipos en la columna derecha'}
              </PendingCount>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <CancelBtn onClick={() => !saving && setModalOpen(false)}>Cancelar</CancelBtn>
                <SaveBtn onClick={guardarVinculaciones} disabled={saving || pendientes === 0}>
                  {saving
                    ? <><i className="fas fa-circle-notch spin" /> Guardando…</>
                    : <><i className="fas fa-save" /> Guardar {pendientes > 0 ? pendientes : ''} vinculación{pendientes !== 1 ? 'es' : ''}</>
                  }
                </SaveBtn>
              </div>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}

      {/* Confirmar desvinculación */}
      {confirmId && (
        <Overlay onClick={buildOnBackdropClick(() => setConfirmId(null))} onMouseDown={onBackdropMouseDown}>
          <ModalBox onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <ModalHead>
              <ModalTitle>Confirmar desvinculación</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={() => setConfirmId(null)}>×</CloseBtn>
            </ModalHead>
            <div style={{ padding: '1rem', fontSize: '0.9rem' }}>
              El equipo dejará de sincronizarse con el proveedor GPS hasta que se vuelva a vincular.
            </div>
            <ModalFoot>
              <div />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <CancelBtn onClick={() => setConfirmId(null)}>Cancelar</CancelBtn>
                <SaveBtn
                  $danger
                  onClick={() => eliminar(confirmId)}
                >
                  Desvincular
                </SaveBtn>
              </div>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}
    </Page>
  );
}
